import { Box, List, ListItemButton, Skeleton, Stack, Typography } from '@mui/material'
import StarOutlinedIcon from '@mui/icons-material/StarOutlined'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, Link } from 'react-router-dom'
import boardApi from '../../api/boardApi'
import { setFavouriteList } from '../../redux/features/favouriteSlice'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useToast } from './ToastProvider'
import { errorMessage } from '../../utils/errors'
import { tokens } from '../../theme'

const FavouriteList = ({ onNavigate }) => {
  const dispatch = useDispatch()
  const toast = useToast()
  const list = useSelector((state) => state.favourites.value)
  const { boardId } = useParams()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    const getBoards = async () => {
      try {
        const res = await boardApi.getFavourites()
        if (active) dispatch(setFavouriteList(res))
      } catch (err) {
        if (active) toast.error(errorMessage(err, 'Could not load your favourites.'))
      } finally {
        if (active) setLoading(false)
      }
    }
    getBoards()
    return () => { active = false }
  }, [dispatch, toast])

  const onDragEnd = async ({ source, destination }) => {
    if (!destination || destination.index === source.index) return

    const previous = list
    const next = [...list]
    const [moved] = next.splice(source.index, 1)
    next.splice(destination.index, 0, moved)

    dispatch(setFavouriteList(next))
    try {
      await boardApi.updateFavouritePosition({ boards: next })
    } catch (err) {
      dispatch(setFavouriteList(previous))
      toast.error(errorMessage(err, 'Could not save the new order.'))
    }
  }

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={0.75} sx={{ pl: 2.5, pr: 1.5, mt: 1 }}>
        <StarOutlinedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant='overline' color='text.disabled'>
          Favourites
        </Typography>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ px: 2, pt: 1 }}>
          {[0, 1].map((key) => (
            <Skeleton key={key} variant='rounded' height={32} />
          ))}
        </Stack>
      ) : list.length === 0 ? (
        <Typography variant='body2' color='text.disabled' sx={{ px: 2.5, pt: 0.5 }}>
          Star a board to pin it here.
        </Typography>
      ) : (
        <List disablePadding sx={{ mt: 0.5 }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='favourites-droppable'>
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {list.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(dragProvided, snapshot) => (
                        <ListItemButton
                          ref={dragProvided.innerRef}
                          {...dragProvided.dragHandleProps}
                          {...dragProvided.draggableProps}
                          selected={item.id === boardId}
                          component={Link}
                          to={`/boards/${item.id}`}
                          onClick={() => onNavigate?.()}
                          sx={{
                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                            boxShadow: snapshot.isDragging ? `0 8px 24px ${tokens.shadow}` : 'none'
                          }}
                        >
                          <Box
                            component='span'
                            sx={{ fontSize: '0.95rem', mr: 1, lineHeight: 1 }}
                            aria-hidden
                          >
                            {item.icon}
                          </Box>
                          <Typography variant='body2' fontWeight={500} noWrap>
                            {item.title || 'Untitled'}
                          </Typography>
                        </ListItemButton>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </List>
      )}
    </Box>
  )
}

export default FavouriteList
